// Which team the user is currently acting as (RFC-1371 D6, RFC-1372 C2/C8).
//
// The active team is CLIENT state, held here and passed explicitly to the two
// operations that need it — project creation and checkout. It is deliberately
// not a server-side "current team" field, because RFC-1372 C8 is emphatic that
// a purchase must freeze its beneficiary on the order row and never re-derive
// it from live UI state at grant time. A server-side active team invites
// exactly that mistake: it makes "read the user's current team" look like a
// legitimate thing for fulfilment to do. Keeping it on the client means the
// only way a team reaches a durable row is as an explicit argument.
//
// C2 is why this is such a small amount of state: the paying wallet for a
// generation is derived from the project, never from here. The active team is
// consulted at creation and clone time and nowhere else, so a wrong value is a
// project in the wrong team — recoverable by cloning — rather than a charge
// against the wrong wallet.
//
// The personal team is the default and the fallback. Someone who never joins a
// team sees exactly today's product: one team, always selected, and (because
// the switcher hides itself at a single team) no switcher at all.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

import { useAuth } from './AuthContext'
import { useMyTeams } from '../hooks/useTeams'
import type { Team } from '../api/teams'

export interface ActiveTeamValue {
  /** Every team the user belongs to, personal first. Empty until loaded. */
  teams: Team[]
  /** The resolved active team, or null while loading / when there are none. */
  activeTeam: Team | null
  /**
   * The personal team the user OWNS (D1). Null only if provisioning has not run.
   *
   * Owned, not merely `kind === 'personal'`: since #3780 a user can be a member
   * of other people's personal teams too, so `kind` alone no longer identifies
   * one row. `uq_teams_personal` is keyed on `created_by`, so exactly one
   * personal team can be owned.
   */
  personalTeam: Team | null
  isLoading: boolean
  /** Ignored for an id the user is not a member of. */
  setActiveTeamId: (teamId: string) => void
}

const DEFAULT: ActiveTeamValue = {
  teams: [],
  activeTeam: null,
  personalTeam: null,
  isLoading: false,
  setActiveTeamId: () => {},
}

/**
 * Exported raw so tests can supply a team context without mounting the
 * provider and its query. Mirrors `FlagsContext`.
 */
export const ActiveTeamContext = createContext<ActiveTeamValue>(DEFAULT)

// Per-user, because this is a shared-browser product and a stale id from
// another account would silently point the next person's new projects at a
// team they are not in. Resolution below rejects that anyway, but keying the
// storage means it never even gets proposed.
function storageKey(userId: string): string {
  return `dw_active_team:${userId}`
}

function readStored(userId: string | undefined): string | null {
  if (userId === undefined) return null
  try {
    return localStorage.getItem(storageKey(userId))
  } catch {
    // Storage can be unavailable (Safari private mode, blocked third-party
    // contexts). Falling back to the personal team is correct, not degraded.
    return null
  }
}

function writeStored(userId: string | undefined, teamId: string): void {
  if (userId === undefined) return
  try {
    localStorage.setItem(storageKey(userId), teamId)
  } catch {
    // Selection still works for this session; only persistence is lost.
  }
}

/** Your own personal team first, then the rest alphabetically.
 *
 * Three tiers, not two. A user can now belong to SEVERAL personal teams — their
 * own plus every one they have been invited into — and the provisioning trigger
 * names every one of them literally `Personal`, so neither `kind` nor `name`
 * separates them. Sorting on `kind` alone put an arbitrary one first, and the
 * first is what `personalTeam` and the active-team fallback below both took.
 */
function orderTeams(teams: Team[]): Team[] {
  const tier = (team: Team): number => {
    if (team.kind === 'personal' && team.myRole === 'owner') return 0
    if (team.kind === 'personal') return 1
    return 2
  }
  return [...teams].sort((a, b) => {
    const byTier = tier(a) - tier(b)
    if (byTier !== 0) return byTier
    return a.name.localeCompare(b.name)
  })
}

/** The personal team the user OWNS, or null. Never someone else's.
 *
 * `myRole === 'owner'` is the load-bearing half. `kind === 'personal'` alone
 * matched the FIRST personal team in the list, which since #3780 can belong to
 * somebody who invited you. This value is the active-team fallback, so that
 * silently made a stranger's personal team the default scope — and
 * `useCreateProject` stamps new projects with the active team, project->team is
 * immutable (D2), and the owning team's wallet pays. Work and spend landed in
 * someone else's team with no way back.
 *
 * Returning null when the user owns none is deliberate: the caller falls
 * through to `teams[0]`, which may still be a team they were invited into, but
 * that is a visible first-in-list choice rather than a value claiming to be
 * "their personal team".
 */
function selectPersonalTeam(teams: Team[]): Team | null {
  return teams.find((team) => team.kind === 'personal' && team.myRole === 'owner') ?? null
}

/** Pure helpers, exported for unit test only. Not part of the public surface. */
export const __testing = { orderTeams, selectPersonalTeam }

export function ActiveTeamProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const userId = user?.id
  const { data, isLoading } = useMyTeams()

  const [requestedTeamId, setRequestedTeamId] = useState<string | null>(() =>
    readStored(userId),
  )

  // Re-read on account change. Without this, switching accounts in one tab
  // carries the previous user's selection into the resolution below, where it
  // would fail membership and silently fall back — correct, but it would also
  // never pick up the NEW account's stored preference.
  useEffect(() => {
    setRequestedTeamId(readStored(userId))
  }, [userId])

  const teams = useMemo(() => orderTeams(data ?? []), [data])
  const personalTeam = useMemo(() => selectPersonalTeam(teams), [teams])

  // Membership is re-checked on every render rather than trusted from storage.
  // Leaving a team elsewhere, or being removed from one, must not leave this
  // pointing at a team the user can no longer act in.
  const activeTeam = useMemo(() => {
    const requested = teams.find((team) => team.id === requestedTeamId)
    if (requested !== undefined) return requested
    if (personalTeam !== null) return personalTeam
    return teams[0] ?? null
  }, [teams, requestedTeamId, personalTeam])

  const setActiveTeamId = useCallback(
    (teamId: string) => {
      setRequestedTeamId(teamId)
      writeStored(userId, teamId)
    },
    [userId],
  )

  const value = useMemo<ActiveTeamValue>(
    () => ({ teams, activeTeam, personalTeam, isLoading, setActiveTeamId }),
    [teams, activeTeam, personalTeam, isLoading, setActiveTeamId],
  )

  return <ActiveTeamContext.Provider value={value}>{children}</ActiveTeamContext.Provider>
}

export function useActiveTeam(): ActiveTeamValue {
  return useContext(ActiveTeamContext)
}
